import { useMutation } from '@apollo/client'
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { differenceInMonths, format } from 'date-fns'
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  Loader,
  Receipt,
  Wrench,
} from 'lucide-react'
import { Suspense, useMemo, useState, useTransition } from 'react'
import { toast } from 'sonner'
import { InvoiceStatusBadge } from '@/components/sv/invoice-status-badge'
import { ToasterContent } from '@/components/sv/toaster-content'
import { H3, Muted } from '@/components/sv/typographies'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { gql } from '@/lib/graphql/gql'
import { useIdCompany, useRBAC, useSuspenseQuery } from '@/lib/hooks'
import { DownloadInvoice } from '@/routes/billing/historical-invoices/download-invoice-button'
import { HistoricalInvoicesFallback } from '@/routes/billing/historical-invoices/fallback'
import { PreviewInvoiceSheet } from '@/routes/billing/historical-invoices/preview-invoice-sheet'
import type {
  CompanyInvoicesQuery,
  CompanyInvoicesQueryVariables,
  ReloadInvoiceMutation,
  ReloadInvoiceMutationVariables,
} from '@/routes/billing/historical-invoices/index.types.gen'

const INVOICES_QUERY = gql`
  query CompanyInvoices($idCompany: String!) {
    company(id: $idCompany) {
      id
      invoices @live {
        id
        idStripeSource
        idStripePaymentIntent
        invoiceNumber
        invoiceType
        invoiceDate
        dueDate
        appliedVat
        showVat
        tax
        taxPercent
        period
        total
        creditsApplied
        createdManually
        chargedAmount
        partial
        status
        currency
        discountAmount
        discountName
        items @live {
          id
          name
          startDate
          endDate
          amount
          currency
          type
        }
      }
    }
  }
`

const RELOAD_INVOICE = gql`
  mutation ReloadInvoice($idCompany: String!) {
    reloadInvoice(idCompany: $idCompany)
  }
`

type Row = NonNullable<
  NonNullable<CompanyInvoicesQuery['company']>['invoices']
>[number]

const HISTORICAL_INVOICES_MONTHS = 12

const ReloadInvoicesAction = ({ idCompany }: { idCompany: string }) => {
  const { rbac } = useRBAC()
  const [isOpen, setIsOpen] = useState(false)
  const [isPending, startTransition] = useTransition()

  const [reloadInvoice] = useMutation<
    ReloadInvoiceMutation,
    ReloadInvoiceMutationVariables
  >(RELOAD_INVOICE)

  if (!rbac.kinsta().superadmin.can()) {
    return null
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(true)}
        disabled={isPending}
      >
        {isPending ? (
          <Loader className="animate-spin size-4" />
        ) : (
          <Wrench className="size-4" />
        )}
        Reload invoices
      </Button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reload all invoices</AlertDialogTitle>
          <AlertDialogDescription>
            Do you want to reload all invoices for this company?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            onClick={() => {
              startTransition(async () => {
                try {
                  await reloadInvoice({ variables: { idCompany } })
                  toast(
                    <ToasterContent variant="success">
                      Invoices reload started
                    </ToasterContent>,
                    { position: 'top-right' }
                  )
                  setIsOpen(false)
                } catch {
                  toast(
                    <ToasterContent variant="error">
                      Failed to reload invoices
                    </ToasterContent>,
                    { position: 'top-right' }
                  )
                }
              })
            }}
          >
            {isPending ? <Loader className="animate-spin" /> : null}
            Continue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

const Content = ({
  onSetInvoice,
}: {
  onSetInvoice?: (invoice: Row) => void
}) => {
  const { idCompany } = useIdCompany()

  const { data } = useSuspenseQuery<
    CompanyInvoicesQuery,
    CompanyInvoicesQueryVariables
  >(INVOICES_QUERY, {
    variables: { idCompany },
  })

  const items = useMemo(
    () =>
      (data?.company?.invoices ?? []).filter(
        (i) => i.invoiceType === 'payAsYouGo' && i.status !== 'upcoming'
      ),
    [data]
  )

  const isExpired = useMemo(() => {
    if (items.length === 0) {
      return true
    }
    const mostRecentDate = items.reduce((latest, invoice) => {
      const date = new Date(invoice.invoiceDate)
      return date > latest ? date : latest
    }, new Date(0))
    return (
      differenceInMonths(new Date(), mostRecentDate) >=
      HISTORICAL_INVOICES_MONTHS
    )
  }, [items])

  const columns: ColumnDef<Row>[] = useMemo(
    () => [
      {
        accessorKey: 'number',
        size: 200,
        header: () => <div className="pl-2">Number</div>,
        cell: ({ row }) =>
          row.original.invoiceNumber ? (
            <div className="pl-4 flex items-center gap-2 font-mono">
              {row.original.invoiceNumber}
              <button
                type="button"
                className="cursor-pointer"
                aria-label={`View invoice ${row.original.invoiceNumber}`}
                onClick={() => onSetInvoice?.(row.original)}
              >
                <Eye className="size-4" aria-hidden="true" />
              </button>
              <DownloadInvoice
                id={row.original.id}
                invoiceNumber={row.original.invoiceNumber}
                idCompany={idCompany}
              />
            </div>
          ) : (
            <div className="pl-4 flex items-center gap-2">
              <span className="italic">No invoice number</span>
              <button
                type="button"
                className="cursor-pointer"
                aria-label="View invoice details"
                onClick={() => onSetInvoice?.(row.original)}
              >
                <Eye className="size-4" aria-hidden="true" />
              </button>
            </div>
          ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <div className="pl-2">
            <InvoiceStatusBadge invoice={row.original} />
          </div>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: () => <div className="pl-2">Created</div>,
        cell: ({ row }) => (
          <Muted className="pl-4 py-2">
            {format(new Date(row.original.invoiceDate), 'MMM d, yyyy')}
          </Muted>
        ),
      },
      {
        accessorKey: 'total',
        header: () => <div className="text-right">Amount</div>,
        cell: ({ row }) => {
          const invoice = row.original
          const total = invoice?.total ?? 0
          const tax = invoice?.tax ?? 0
          const credits = invoice?.creditsApplied ?? 0
          const discountAmount = invoice?.discountAmount ?? 0

          const finalPrice = total + tax + credits

          let appliedDiscount = discountAmount
          if (finalPrice < discountAmount) {
            appliedDiscount = finalPrice
          }

          const grandTotal = (finalPrice - appliedDiscount) / 100
          return <div className="text-right pr-2">${grandTotal.toFixed(2)}</div>
        },
      },
    ],
    [idCompany, onSetInvoice]
  )

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  if (isExpired) {
    return null
  }

  return (
    <Card className="bg-secondary p-0 gap-0">
      <CardHeader className="px-5 pt-3 pb-1 items-center">
        <CardTitle>
          <H3 className="flex items-center gap-2 text-sm text-muted-foreground">
            <Receipt className="size-4" /> Historical invoices
          </H3>
        </CardTitle>
        <CardAction className="self-center">
          <ReloadInvoicesAction idCompany={idCompany} />
        </CardAction>
      </CardHeader>
      <CardContent className="p-0">
        <Card className="p-0 border-x-0 border-b-0 shadow-none">
          <CardHeader className="pt-6">
            <CardDescription>
              These invoices were created before May 1, 2026. New invoices are
              available through the manage billing link above. Please download
              any old invoices you need, they won't be available in the
              dashboard anymore.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-4">
              <div className="rounded-md border">
                <Table className="table-fixed">
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow
                        key={headerGroup.id}
                        className="bg-muted/50 hover:bg-muted/50"
                      >
                        {headerGroup.headers.map((header) => (
                          <TableHead
                            style={{ width: header.getSize() }}
                            key={header.id}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {table.getRowModel().rows?.length
                      ? table.getRowModel().rows.map((row) => (
                          <TableRow key={row.id}>
                            {row.getVisibleCells().map((cell) => (
                              <TableCell
                                className="p-0 m-0"
                                key={cell.id}
                                style={{ maxWidth: cell.column.getSize() }}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      : null}
                    {!table.getRowModel().rows?.length ? (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-24 text-center"
                        >
                          No invoices found.
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
          <CardFooter className="pb-6">
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                onClick={table.previousPage}
                disabled={!table.getCanPreviousPage()}
              >
                <span className="sr-only">Go to previous page</span>
                <ChevronLeft />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="size-8"
                onClick={table.nextPage}
                disabled={!table.getCanNextPage()}
              >
                <span className="sr-only">Go to next page</span>
                <ChevronRight />
              </Button>
            </div>
          </CardFooter>
        </Card>
      </CardContent>
    </Card>
  )
}

export const HistoricalInvoices = () => {
  const [invoice, setInvoice] = useState<Row | null>(null)

  return (
    <>
      <Suspense fallback={<HistoricalInvoicesFallback />}>
        <Content onSetInvoice={(invoice) => setInvoice(invoice)} />
      </Suspense>
      <PreviewInvoiceSheet
        invoice={invoice}
        isOpen={Boolean(invoice)}
        onClose={() => setInvoice(null)}
      />
    </>
  )
}
